const STORAGE_KEY = 'android-app-banner-dismissed-at';
const DISMISS_DURATION_MS = 1000 * 60 * 60 * 24 * 7; // 7 days

type Nullable<T> = T | null;

function isAndroid(): boolean {
  const { userAgent } = navigator;
  return /android/i.test(userAgent || '');
}

function inStandaloneMode(): boolean {
  const nav = navigator as Navigator & { standalone?: boolean };
  return matchMedia('(display-mode: standalone)').matches || !!nav.standalone;
}

function isInAppWebView(): boolean {
  return document.body.classList.contains('app');
}

function getStoredValue(): Nullable<number> {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value ? Number.parseInt(value, 10) : null;
  } catch (_error) {
    return null;
  }
}

function setStoredValue(timestamp: number): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(timestamp));
  } catch (_error) {
    // no-op
  }
}

function clearStoredValue(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (_error) {
    // no-op
  }
}

class AndroidAppBanner {
  private readonly banner: HTMLElement;

  private openButton: Nullable<HTMLButtonElement> = null;

  private closeButton: Nullable<HTMLButtonElement> = null;

  private readonly storeUrl: string;

  private readonly universalLink: string;

  private readonly pathParts: { pathname: string; search: string };

  private readonly packageName?: string;

  private readonly hiddenClass = 'hidden';

  private isInitialized = false;

  constructor(banner: HTMLElement) {
    this.banner = banner;
    this.storeUrl = banner.dataset.storeUrl || '';
    this.universalLink = location.origin;
    this.pathParts = this.parsePath(banner.dataset.path);
    this.packageName = banner.dataset.packageName;
  }

  init(): void {
    if (this.isInitialized) {
      this.refresh();
      return;
    }

    this.openButton = this.banner.querySelector<HTMLButtonElement>('[data-role="open"]');
    this.closeButton = this.banner.querySelector<HTMLButtonElement>('[data-role="close"]');

    this.openButton?.addEventListener('click', this.handleOpenClick);
    this.closeButton?.addEventListener('click', this.handleCloseClick);

    this.isInitialized = true;
    this.refresh();
  }

  refresh(): void {
    if (!this.shouldDisplay()) {
      this.hide(false);
      return;
    }

    this.show();
  }

  private removeClassFromBody() {
    document.body.classList.remove('with-app-banner');
  }

  private addClassToBody() {
    document.body.classList.add('with-app-banner');
  }

  private shouldDisplay(): boolean {
    if (!isAndroid()) {
      return false;
    }

    if (isInAppWebView() || inStandaloneMode()) {
      return false;
    }

    if (!this.storeUrl || !this.universalLink) {
      return false;
    }

    const dismissedAt = getStoredValue();
    if (dismissedAt) {
      const elapsed = Date.now() - dismissedAt;
      if (elapsed < DISMISS_DURATION_MS) {
        return false;
      }

      // Expired dismissal. Clear stored value so it can be shown again in future.
      clearStoredValue();
    }

    return true;
  }

  private show(): void {
    if (!this.banner.classList.contains(this.hiddenClass)) {
      return;
    }

    this.addClassToBody();
    this.banner.classList.remove(this.hiddenClass);
    this.banner.setAttribute('aria-hidden', 'false');
  }

  private hide(storeDismissal: boolean): void {
    this.removeClassFromBody();
    if (!this.banner.classList.contains(this.hiddenClass)) {
      this.banner.classList.add(this.hiddenClass);
      this.banner.setAttribute('aria-hidden', 'true');
    }

    if (storeDismissal) {
      setStoredValue(Date.now());
    }
  }

  private openApp(): void {
    if (!this.storeUrl) {
      return;
    }

    const intentLink = this.buildIntentLink();
    if (intentLink) {
      location.href = intentLink;
      return;
    }

    location.href = this.storeUrl;
  }

  private handleOpenClick = (): void => {
    this.openApp();
  };

  private handleCloseClick = (): void => {
    this.hide(true);
  };

  private parsePath(path?: string): { pathname: string; search: string } {
    if (!path) {
      return { pathname: '/', search: '' };
    }

    const trimmed = path.trim();
    if (!trimmed) {
      return { pathname: '/', search: '' };
    }

    const hashIndex = trimmed.indexOf('#');
    const withoutHash = hashIndex >= 0 ? trimmed.slice(0, hashIndex) : trimmed;

    const [rawPath = '', rawQuery] = withoutHash.split('?');
    const pathname = rawPath.startsWith('/') ? rawPath : `/${rawPath}`;
    const search = rawQuery ? `?${rawQuery}` : '';

    return { pathname, search };
  }

  private buildIntentLink(): string | null {
    if (!this.universalLink || !this.storeUrl || !this.packageName) {
      return null;
    }

    try {
      const url = new URL(this.universalLink);
      url.pathname = this.pathParts.pathname;
      url.search = this.pathParts.search;

      const protocol = url.protocol.replace(':', '') || 'https';
      const fallbackUrl = encodeURIComponent(this.storeUrl);
      const intentPath = `${url.host}${url.pathname}${url.search}`;

      return `intent://${intentPath}#Intent;scheme=${protocol};package=${this.packageName};S.browser_fallback_url=${fallbackUrl};end`;
    } catch (_error) {
      return null;
    }
  }
}


const BANNER_ELEMENT_ID = 'android-app-banner';
const CONTROLLER_KEY = '__androidAppBannerController';

type BannerElement = HTMLElement & {
  [CONTROLLER_KEY]?: AndroidAppBanner;
};

function initializeBanner(): void {
  const banner = document.getElementById(BANNER_ELEMENT_ID) as Nullable<BannerElement>;
  if (!banner) {
    return;
  }

  if (banner[CONTROLLER_KEY]) {
    banner[CONTROLLER_KEY]?.refresh();
    return;
  }

  const controller = new AndroidAppBanner(banner);
  controller.init();
  banner[CONTROLLER_KEY] = controller;
}

export function setupAndroidAppBanner(): void {
  if (!isAndroid()) {
    return;
  }

  if (isInAppWebView() || inStandaloneMode()) {
    return;
  }

  const onReady = () => initializeBanner();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady, { once: true });
  } else {
    onReady();
  }

  document.addEventListener('turbo:load', initializeBanner);
}

setupAndroidAppBanner();
